import { BreakpointObserver, MediaMatcher } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { NavigationComponent, NavigationItem } from '@claas/claas-ui-components/navigation/navigation';
import { AlertBannerComponent } from '@claas/claas-ui-components/notification/alert-banner';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { IMaintenanceText, MaintenanceToast } from '@cde-fe-app/app/shared/models/dashboard.model';
import { LocalStorageService } from '@cde-fe-app/app/shared/services/local-storage.service';
import { MaintenanceViewType } from '@cde-fe-app/app/shared/services/maintenance-text.service';
import { RoutingStateService } from '@cde-fe-app/app/shared/services/routing-state.service';
import * as RootState from '@cde-fe-app/app/shared/state';
import { featureToggleStore } from '@cde-fe-app/app/shared/state/feature-toggle/feature-toggle.store';
import { uiStore } from '@cde-fe-app/app/shared/state/ui/ui.store';
import { SpinnerComponent } from '@cde-fe-app/app/spinner/spinner/spinner.component';
import { environment } from '@cde-fe-app/environments/environment';

@Component({
  selector: 'cde-sidenav',
  templateUrl: './cde-sidenav.component.html',
  styleUrls: ['./cde-sidenav.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AlertBannerComponent, NavigationComponent, RouterOutlet, SpinnerComponent, TranslateModule],
})
export class CdeSidenavComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly media = inject(MediaMatcher);
  readonly store = inject<Store<RootState.IState>>(Store<RootState.IState>);
  readonly uiStore = inject(uiStore);
  readonly cdr = inject(ChangeDetectorRef);
  readonly translate = inject(TranslateService);
  readonly localStorageService = inject(LocalStorageService);
  readonly breakpointObserver = inject(BreakpointObserver);
  readonly featureToggleStore = inject(featureToggleStore);
  readonly routingState = inject(RoutingStateService);

  public cssTopOffset = computed(() => (this.routingState.isHideHeaderOnMobileRoute() ? '0px' : '56px'));

  @ViewChildren('toastsWrapper') toastsWrapper: QueryList<ElementRef>;

  public isExpanded = model.required<boolean>();
  public sideNavItems = input.required<NavigationItem[]>();
  public orgRegistered = input.required<boolean>();

  public pendingOrgSwitch = input<boolean | null>(null);
  public pendingOrgRegistration = input<boolean | null>(null);
  public rehydratePending = input<boolean | null>(null);

  public orgIsRejected = input<boolean>(false);
  public validatedOrg = input<boolean>(false);
  public internalOrg = input<boolean>(false);

  public orgId = input<string>();
  public clickedEvent = output<boolean>();

  public contentHeight$$ = this.uiStore.contentHeight;

  public maintenanceToasts: MaintenanceToast[] = [];

  public hideValidatedOrgAlertBanner = signal(false);
  public hideRejectedOrgAlertBanner = signal(false);

  public mobileQuery: MediaQueryList;
  public showOrgVerificationBanner = computed(() => {
    this.applyContentWrapperHeight(this.toastsWrapper);

    return !this.validatedOrg() && this.orgRegistered() && !this.internalOrg() && !this.hideValidatedOrgAlertBanner();
  });

  private subscription = new Subscription();

  constructor() {
    this.mobileQuery = this.media.matchMedia('(max-width: 599px)');
  }

  ngOnInit(): void {
    this.subscription.add(
      this.localStorageService.observeKey('maintenanceTexts').subscribe((maintenanceTexts: IMaintenanceText[]) => {
        this.setMaintenanceToasts(maintenanceTexts);
      })
    );
  }

  public ngAfterViewInit(): void {
    this.applyContentWrapperHeight(this.toastsWrapper);

    this.toastsWrapper.changes.subscribe((elements: QueryList<ElementRef>) => {
      this.applyContentWrapperHeight(elements);
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  closeAlertWarning(id: string) {
    let maintenanceTexts: IMaintenanceText[] = this.localStorageService.getItem('maintenanceTexts');
    if (maintenanceTexts.length > 0) {
      maintenanceTexts = maintenanceTexts.filter(mt => mt.id !== id);
      if (maintenanceTexts.length === 0) {
        this.localStorageService.removeItem('maintenanceTexts');
      } else {
        this.localStorageService.setItem('maintenanceTexts', maintenanceTexts);
      }
    }
    this.cdr.detectChanges();
    this.applyContentWrapperHeight(this.toastsWrapper);
  }

  closeRejectedOrgAlertBanner() {
    this.hideRejectedOrgAlertBanner.set(true);
    this.cdr.detectChanges();
    this.applyContentWrapperHeight(this.toastsWrapper);
  }

  closeUnverifiedOrgAlertBanner() {
    this.hideValidatedOrgAlertBanner.set(true);
  }

  deleteOrg() {
    window.open(
      `${environment.claasIdDashboardBaseUrl}/organisation-data?orgId=${this.orgId()}&startDeletion=true`,
      '_blank'
    );
  }

  private applyContentWrapperHeight(toastsWrapper: QueryList<ElementRef>) {
    if (!toastsWrapper) {
      return;
    }

    requestAnimationFrame(() => {
      let totalHeight = 0;
      toastsWrapper.forEach(element => {
        if (element.nativeElement) {
          const elementHeight = element.nativeElement.offsetHeight;
          if (elementHeight > 0) {
            totalHeight += elementHeight; // + 48; <- not needed anymore because there is no margin anymore between the banners
          }
        }
      });

      const height = totalHeight > 0 ? `calc(100% - ${totalHeight}px)` : '100%';
      this.uiStore.setContentHeight(height);
      this.cdr.detectChanges();
    });
  }

  private setMaintenanceToasts(maintenanceTexts: IMaintenanceText[]) {
    this.maintenanceToasts = [];
    maintenanceTexts?.forEach(maintenanceText => {
      const newMaintenanceToast = {
        ...maintenanceText,
        type: maintenanceText.viewType as MaintenanceViewType,
      };

      const isDuplicate = this.maintenanceToasts.some(toast => toast.id === newMaintenanceToast.id);
      if (!isDuplicate) {
        this.maintenanceToasts.push(newMaintenanceToast);
      }
    });
    this.cdr.detectChanges();
    this.applyContentWrapperHeight(this.toastsWrapper);
  }
}
